/** Small fetch wrapper: JSON in and out, CSRF header, typed errors. */

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    /** Extra detail some errors carry (e.g. the visits in the way). */
    public details?: unknown,
  ) {
    super(message);
  }
}

type Listener = (err: ApiError) => void;
const unauthorizedListeners = new Set<Listener>();

/** Notified when the server says the session is gone (signed out or timed out). */
export function onUnauthorized(fn: Listener) {
  unauthorizedListeners.add(fn);
  return () => {
    unauthorizedListeners.delete(fn);
  };
}

export async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(method !== "GET" ? { "x-vaulted-csrf": "1" } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const err = new ApiError(res.status, data?.error?.code ?? "error", data?.error?.message ?? "Something went wrong.", data?.error?.details);
    if (res.status === 401 && !path.startsWith("/api/auth/") && err.code !== "reauth_required") {
      unauthorizedListeners.forEach((fn) => fn(err));
    }
    throw err;
  }
  return data as T;
}

export const get = <T>(path: string) => api<T>("GET", path);
export const post = <T>(path: string, body: unknown = {}) => api<T>("POST", path, body);
export const patch = <T>(path: string, body: unknown) => api<T>("PATCH", path, body);
export const put = <T>(path: string, body: unknown) => api<T>("PUT", path, body);
export const del = <T>(path: string) => api<T>("DELETE", path);

export function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : "Something went wrong.";
}
